import { buildTerrain, type LevelDef, type LevelStyle, type Material, type Shape } from './levels';

/**
 * Tunnel maps: underground arenas that are mostly solid rock with a ceiling, where the crews
 * start in chambers at the two ends and the way between is a maze of tunnels and caverns. Every
 * room and tunnel is carved out of one big slab with holes (a tunnel is a row of holes, a room is
 * a grid of them), and props, rails and columns are laid in afterwards so the holes do not eat
 * them. The pickaxe, the tunneler and TNT come into their own here, but every map is walkable end
 * to end as authored. The carving has one rule the layouts follow: a tunnel's floor is the lowest
 * surface in its column, so a ramp that leaves a drift part way along, or doubles back from its
 * end, undercuts that drift's floor and leaves a cliff. The way across is therefore one run of
 * drifts, ramps and rooms that always carries on in the same direction, with the lower levels
 * reached by one-way drops (shafts, holes in the turf) and left by digging. Spawns are authored
 * by hand (the generator's placer looks for the top-most surface in a column, which under a
 * ceiling is the roof of the map) and snapped to the floor they stand on; client/test/pathing.test.ts
 * holds the way across.
 */

const CAVE: LevelStyle = {
  sky: ['#120d18', '#2a1f33'], far: '#1a1322', near: '#241a2e', water: 'transparent', deep: '#07050a', pit: '#07050a', horizon: 'cave', art: 'cave',
};

/** Radius of the holes a tunnel is bored with: a mole is 56 tall, so an 80 px bore leaves headroom to jump a little. */
const BORE = 40;

/** A tunnel: a row of holes along a polyline, spaced closely enough that the floor's scallops are under 2 px. */
function bore(pts: [number, number][], r = BORE): Shape[] {
  const out: Shape[] = [];
  const step = r * 0.45;
  for (let i = 0; i + 1 < pts.length; i++) {
    const [x0, y0] = pts[i]!;
    const [x1, y1] = pts[i + 1]!;
    const len = Math.hypot(x1 - x0, y1 - y0);
    const n = Math.max(1, Math.ceil(len / step));
    for (let k = 0; k <= n; k++) {
      const t = k / n;
      out.push({ kind: 'hole', x: Math.round(x0 + (x1 - x0) * t), y: Math.round(y0 + (y1 - y0) * t), r });
    }
  }
  return out;
}

/**
 * A room: holes on a grid filling the box, with corners rounded by the hole radius. The floor is
 * at y + h, flat to within 2 px, so a crew can stand anywhere along it.
 */
function room(x: number, y: number, w: number, h: number, r = 48): Shape[] {
  const out: Shape[] = [];
  const rr = Math.min(r, w / 2, h / 2);
  const step = rr * 0.5;
  const nx = Math.max(1, Math.ceil((w - 2 * rr) / step));
  const ny = Math.max(1, Math.ceil((h - 2 * rr) / step));
  for (let i = 0; i <= nx; i++) {
    for (let j = 0; j <= ny; j++) {
      out.push({ kind: 'hole', x: Math.round(x + rr + ((w - 2 * rr) * i) / nx), y: Math.round(y + rr + ((h - 2 * rr) * j) / ny), r: rr });
    }
  }
  return out;
}

/** An arched room: a flat-floored box with a round vault over it (the catacombs). */
function vault(x: number, y: number, w: number, h: number): Shape[] {
  const r = Math.min(w / 2, 90);
  return [...room(x, y + r * 0.6, w, h - r * 0.6, 40), ...bore([[x + r, y + r], [x + w - r, y + r]], r)];
}

/** A hanging mine prop: a lintel along a tunnel roof with a stub post under each end. Short enough to walk under. */
function prop(x: number, roofY: number, w = 64): Shape[] {
  return [
    { kind: 'rect', x: x - w / 2, y: roofY, w, h: 8, m: 'wood' },
    { kind: 'rect', x: x - w / 2, y: roofY + 8, w: 8, h: 14, m: 'wood' },
    { kind: 'rect', x: x + w / 2 - 8, y: roofY + 8, w: 8, h: 14, m: 'wood' },
  ];
}

/** A ladder up the wall of a dead-end shaft: thin rungs, decoration a mole climbs by jumping. */
function ladder(x: number, y0: number, y1: number): Shape[] {
  const out: Shape[] = [];
  for (let y = y0; y < y1; y += 60) out.push({ kind: 'rect', x, y, w: 22, h: 4, m: 'wood' });
  return out;
}

/** A rail along a drift floor: one thin metal strip, a cue that carts once ran here. */
function rail(x: number, floorY: number, w: number): Shape {
  return { kind: 'rect', x, y: floorY - 4, w, h: 4, m: 'metal' };
}

/** Cracks in a wall: a scatter of small holes that do not break through. */
function crumble(x: number, y: number, w: number, h: number, n: number, seed: number): Shape[] {
  const out: Shape[] = [];
  let s = seed;
  const rnd = (): number => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
  for (let i = 0; i < n; i++) out.push({ kind: 'hole', x: Math.round(x + rnd() * w), y: Math.round(y + rnd() * h), r: Math.round(8 + rnd() * 10) });
  return out;
}

/** The mirror image of shapes across the middle of a level `width` wide. */
function mirror(shapes: Shape[], width: number): Shape[] {
  return shapes.map((s): Shape => {
    switch (s.kind) {
      case 'rect':
        return { ...s, x: width - s.x - s.w };
      case 'circle':
      case 'hole':
        return { ...s, x: width - s.x };
      case 'poly':
        return { ...s, pts: s.pts.map(([x, y]) => [width - x, y] as [number, number]).reverse() };
      default:
        return s;
    }
  });
}

function mirrorSpots(spots: [number, number][], width: number): [number, number][] {
  return spots.map(([x, y]) => [width - x, y]);
}

/** Authored spawn spots snapped onto the floor under them, so a 2 px scallop never leaves a mole in the air. */
function snapSpawns(level: LevelDef): void {
  const t = buildTerrain(level);
  const snap = (list: [number, number][]): [number, number][] =>
    list.map(([x, y]) => {
      const ground = t.surfaceY(x, Math.max(0, y - 60));
      return [x, ground < t.h ? ground - 1 : y];
    });
  level.spawns = level.spawns.map(snap);
  level.floats = level.floats.map(snap);
}

export interface TunnelMapDef {
  /** The map id players pick and the lobby sends: `cave:` plus a short name. */
  id: string;
  name: string;
  build: () => LevelDef;
}

// ---------- Burrow ----------

/**
 * A grass map with a thin surface over a wide warren. The way across is the upper warren: a
 * drift from each home chamber, a ramp down and the central cavern. Two holes in the turf drop
 * into the drifts. Under it all is a hall reached by a shaft at the far end of each chamber: a
 * fall of 220 px, and the only way back up is to dig (80 px of rock separates the hall's roof
 * from the cavern floor).
 */
function burrow(): LevelDef {
  const W = 2600;
  const H = 1300;
  const SURF = 200;
  const rock: Material = 'rock';
  const left: Shape[] = [
    // The home chamber, the drift east out of it and the ramp down to the cavern.
    ...room(240, 290, 340, 150),
    ...bore([[560, 400], [780, 400]]),
    ...bore([[780, 400], [1120, 660]]),
    // Surface entrance: a slope from the turf that drops into the drift.
    ...bore([[600, 160], [680, 400]], 36),
    // The shaft at the chamber's far end, the tier-two drift under the chamber and the ramp down to the hall.
    ...bore([[260, 400], [160, 400]]),
    ...bore([[160, 400], [160, 620]], 34),
    ...bore([[160, 620], [640, 620]]),
    ...bore([[640, 620], [1000, 920]]),
  ];
  const mid: Shape[] = [
    // The central cavern on the way across, and the hall beneath it.
    ...room(1120, 500, 360, 200, 56),
    ...room(1000, 780, 600, 180, 56),
  ];
  const props: Shape[] = [
    // Columns hanging from the cavern roofs, a mole's height clear of the floor: cover to chip at.
    { kind: 'rect', x: 1240, y: 500, w: 30, h: 128, m: rock },
    { kind: 'rect', x: 1330, y: 500, w: 30, h: 128, m: rock },
    { kind: 'rect', x: 1100, y: 780, w: 30, h: 108, m: rock },
    { kind: 'rect', x: 1250, y: 780, w: 30, h: 108, m: rock },
    { kind: 'rect', x: 1400, y: 780, w: 30, h: 108, m: rock },
    // Boulders on the hall floor.
    { kind: 'circle', x: 1180, y: 960, r: 18, m: rock },
    { kind: 'circle', x: 1480, y: 960, r: 22, m: rock },
    // Roots hanging into the home chambers.
    { kind: 'rect', x: 330, y: 290, w: 24, h: 60, m: rock },
    { kind: 'rect', x: 2246, y: 290, w: 24, h: 60, m: rock },
  ];
  const level: LevelDef = {
    id: 'cave:burrow', name: 'Burrow', width: W, height: H, waterY: null, gravity: 1, stamina: 1.3, jump: 1, style: CAVE,
    blurb: 'A thin lawn over a warren. The drifts meet in a cavern in the middle; the shafts at the ends drop into a hall beneath it, and only digging gets you back up.',
    shapes: [
      { kind: 'rect', x: 0, y: SURF, w: W, h: 130, m: 'grass' },
      { kind: 'rect', x: 0, y: SURF + 130, w: W, h: H - SURF - 130, m: rock },
      ...left, ...mirror(left, W), ...mid, ...props,
      ...crumble(1180, 400, 240, 60, 6, 11),
    ],
    boats: [],
    spawns: [
      [[510, 440], [450, 440], [360, 440], [290, 440]],
      mirrorSpots([[510, 440], [450, 440], [360, 440], [290, 440]], W),
      [[1200, 700], [1400, 700], [1300, 700], [1300, 960]],
    ],
    floats: [[[400, 660], [500, 660]], [[2200, 660], [2100, 660]], [[1500, 960]]],
  };
  snapSpawns(level);
  return level;
}

// ---------- Old Mines ----------

/**
 * Stone drifts shored with wooden props, rails along the floors, ore pockets in the walls and a
 * deep pit under the middle where the lower workings fell in. The way across is the upper drift
 * through the haulage cavern. A shaft at the far end of each pit head drops into the lower
 * drift, which runs to the pit's edge and a plank walk over it; from down there only digging
 * gets you back up.
 */
function mines(): LevelDef {
  const W = 2700;
  const H = 1350;
  const stone: Material = 'stone';
  const left: Shape[] = [
    // The pit head and the upper drift east into the haulage cavern.
    ...room(200, 300, 520, 160),
    ...bore([[700, 420], [1150, 420]]),
    // The shaft at the far end of the pit head and the lower drift to the pit's edge.
    ...bore([[220, 420], [120, 420]]),
    ...bore([[120, 420], [120, 620]], 34),
    ...bore([[120, 660], [1220, 660]]),
    // A shaft up from the lower drift to an ore pocket just under the upper drift, with a ladder.
    ...bore([[900, 660], [900, 560]], 34),
    ...room(800, 480, 200, 80, 36),
    // The pit's mouth under the plank walk.
    ...room(1230, 640, 240, 760, 60),
  ];
  const mid: Shape[] = [
    // The haulage cavern in the middle of the upper level.
    ...room(1150, 300, 400, 170, 60),
    // The collapsed lower workings: a wide hollow that falls out of the bottom of the map.
    ...room(1000, 900, 700, 500, 70),
  ];
  const props: Shape[] = [
    // Props along the drifts.
    ...prop(800, 362), ...prop(1000, 362), ...prop(1900, 362), ...prop(1700, 362),
    ...prop(500, 602), ...prop(760, 602), ...prop(1080, 602), ...prop(2200, 602), ...prop(1940, 602), ...prop(1620, 602),
    // Rails on the floors, stopping short of the pit.
    rail(740, 460, 360), rail(1600, 460, 360), rail(180, 700, 1000), rail(1520, 700, 1000),
    // Ore carts left on the upper rails: low enough to step onto.
    { kind: 'rect', x: 860, y: 440, w: 56, h: 16, m: 'metal' },
    { kind: 'rect', x: 1784, y: 440, w: 56, h: 16, m: 'metal' },
    // The plank walk over the pit, with a post hung under each end.
    { kind: 'rect', x: 1180, y: 692, w: 340, h: 12, m: 'wood' },
    { kind: 'rect', x: 1240, y: 704, w: 10, h: 50, m: 'wood' },
    { kind: 'rect', x: 1450, y: 704, w: 10, h: 50, m: 'wood' },
    // Ladders up the ore shafts.
    // (Kept above a mole's head: a rung across the lower drift would seal it.)
    ...ladder(872, 520, 620), ...ladder(1806, 520, 620),
    // Ore pockets in the solid between the drifts.
    { kind: 'circle', x: 650, y: 560, r: 30, m: 'rock' },
    { kind: 'circle', x: 1050, y: 560, r: 22, m: 'rock' },
    { kind: 'circle', x: 2050, y: 560, r: 30, m: 'rock' },
    { kind: 'circle', x: 1650, y: 560, r: 22, m: 'rock' },
    { kind: 'circle', x: 1350, y: 180, r: 40, m: 'rock' },
    { kind: 'circle', x: 700, y: 1000, r: 44, m: 'rock' },
    { kind: 'circle', x: 2000, y: 1000, r: 44, m: 'rock' },
    // Timber columns hanging in the haulage cavern, a mole's height clear of the floor.
    { kind: 'rect', x: 1260, y: 300, w: 24, h: 98, m: 'wood' },
    { kind: 'rect', x: 1416, y: 300, w: 24, h: 98, m: 'wood' },
  ];
  const level: LevelDef = {
    id: 'cave:mines', name: 'Old Mines', width: W, height: H, waterY: null, gravity: 1, stamina: 1.3, jump: 1, style: CAVE,
    blurb: 'Timbered drifts on two levels and a bottomless pit where the lower workings fell in. The upper drift is the way across; the shafts at the ends drop to the plank walk over the pit.',
    shapes: [
      { kind: 'rect', x: 0, y: 0, w: W, h: H, m: stone },
      ...left, ...mirror(left, W), ...mid, ...props,
      ...crumble(1200, 500, 300, 60, 5, 7),
    ],
    boats: [],
    spawns: [
      [[650, 460], [560, 460], [470, 460], [380, 460]],
      mirrorSpots([[650, 460], [560, 460], [470, 460], [380, 460]], W),
      [[1230, 470], [1470, 470], [1350, 470], [1000, 700]],
    ],
    floats: [[[400, 700], [560, 700]], [[2300, 700], [2140, 700]], [[1100, 700]]],
  };
  snapSpawns(level);
  return level;
}

// ---------- Catacombs ----------

/**
 * Long stone corridors with arched rooms off them, a tall crypt chamber in the middle, niches in
 * the walls and crumbling masonry. The upper corridor is the way across: home crypt, side chapel,
 * and the balcony over the crypt. A shaft at the back of each home crypt drops to the lower
 * corridor, which runs under everything to the crypt floor; from there only digging goes up.
 */
function catacombs(): LevelDef {
  const W = 2500;
  const H = 1250;
  const stone: Material = 'stone';
  const left: Shape[] = [
    // The home crypt, the upper corridor east, a chapel off it and the corridor on to the crypt.
    ...vault(120, 240, 440, 200),
    ...bore([[540, 400], [700, 400]], 36),
    ...vault(700, 250, 240, 190),
    ...bore([[940, 400], [1000, 400]], 36),
    // The shaft at the back of the home crypt and the lower corridor to the crypt floor.
    ...bore([[130, 400], [70, 400]], 36),
    ...bore([[70, 400], [70, 560]], 30),
    ...bore([[70, 604], [1000, 604]], 36),
    // Side niches off the lower corridor.
    ...vault(300, 470, 160, 170),
    ...vault(620, 470, 160, 170),
  ];
  const mid: Shape[] = [
    // The central crypt chamber, tall, with the upper corridor crossing it on a balcony.
    ...vault(1000, 230, 500, 410),
  ];
  const props: Shape[] = [
    // The crypt's balcony: the upper corridor's floor carried across the chamber on stone, on
    // posts that stop a mole's height short of the crypt floor.
    { kind: 'rect', x: 1000, y: 440, w: 500, h: 18, m: stone },
    { kind: 'rect', x: 1060, y: 458, w: 24, h: 110, m: stone },
    { kind: 'rect', x: 1416, y: 458, w: 24, h: 110, m: stone },
    // Tombs on the crypt floor and in the home crypts.
    { kind: 'rect', x: 1180, y: 600, w: 80, h: 40, m: stone },
    { kind: 'rect', x: 160, y: 410, w: 70, h: 30, m: stone },
    { kind: 'rect', x: 2270, y: 410, w: 70, h: 30, m: stone },
    // Low metal grates at the balcony ends: bars a mole steps over.
    { kind: 'rect', x: 1004, y: 432, w: 6, h: 8, m: 'metal' },
    { kind: 'rect', x: 1490, y: 432, w: 6, h: 8, m: 'metal' },
  ];
  const level: LevelDef = {
    id: 'cave:catacombs', name: 'Catacombs', width: W, height: H, waterY: null, gravity: 1, stamina: 1.3, jump: 1, style: CAVE,
    blurb: 'Corridors and arched rooms around a tall crypt. The upper corridor crosses the crypt on a balcony; the shafts at the back of the home crypts drop to the lower way beneath it.',
    shapes: [
      { kind: 'rect', x: 0, y: 0, w: W, h: H, m: stone },
      ...left, ...mirror(left, W), ...mid, ...props,
      ...crumble(560, 470, 120, 50, 5, 3), ...crumble(1820, 470, 120, 50, 5, 5), ...crumble(1100, 690, 300, 40, 6, 9),
    ],
    boats: [],
    spawns: [
      [[520, 440], [430, 440], [340, 440], [250, 440]],
      mirrorSpots([[520, 440], [430, 440], [340, 440], [250, 440]], W),
      [[1100, 640], [1400, 640], [1320, 640], [1250, 440]],
    ],
    floats: [[[300, 640], [500, 640]], [[2200, 640], [2000, 640]], [[1050, 640]]],
  };
  snapSpawns(level);
  return level;
}

export const TUNNEL_MAPS: readonly TunnelMapDef[] = [
  { id: 'cave:burrow', name: 'Burrow', build: burrow },
  { id: 'cave:mines', name: 'Old Mines', build: mines },
  { id: 'cave:catacombs', name: 'Catacombs', build: catacombs },
];

export const TUNNEL_MAP_IDS: readonly string[] = TUNNEL_MAPS.map((m) => m.id);

const BY_ID = new Map(TUNNEL_MAPS.map((m) => [m.id, m]));

export function isTunnelMap(id: string): boolean {
  return BY_ID.has(id);
}

const cache = new Map<string, LevelDef>();

/** The level for a tunnel map id, built once per session (and identically on every client). */
export function tunnelLevel(id: string): LevelDef | null {
  const def = BY_ID.get(id);
  if (!def) return null;
  const hit = cache.get(id);
  if (hit) return hit;
  const level = def.build();
  cache.set(id, level);
  return level;
}

/** Tags the map picker filters by. */
export function tunnelTags(): string[] {
  return ['land', 'tunnels'];
}
